import React from 'react';
import { api } from 'common';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Drawer from '@mui/material/Drawer';
import { useThemeApp } from './Theme/theme';
import ListItem from '@mui/material/ListItem';
import { useTranslation } from "react-i18next";
import IconButton from '@mui/material/IconButton';
import MoneyIcon from '@mui/icons-material/Money';
import Typography from '@mui/material/Typography';
import CssBaseline from '@mui/material/CssBaseline';
import ExitIcon from '@mui/icons-material/ExitToApp';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import OfferIcon from '@mui/icons-material/LocalOffer';
import { useSelector, useDispatch } from "react-redux";
import CarIcon from '@mui/icons-material/DirectionsCar';
import ViewListIcon from '@mui/icons-material/ViewList';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListItemButton from '@mui/material/ListItemButton';
import { useTheme, AppBar, Toolbar } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { Outlet, Link, useLocation } from 'react-router-dom';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import NotifyIcon from '@mui/icons-material/NotificationsActive';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { CampaignSharp, CardGiftcard, MenuOpen, Place } from '@mui/icons-material';

interface ResponsiveDrawerProps {
  children?: React.ReactNode;
}

const DRAWER_WIDTH = 260;
const TOOLBAR_HEIGHT = 64;

export default function ResponsiveDrawer({ children }: ResponsiveDrawerProps) {
  const { signOff } = api;
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const { colors } = useThemeApp();
  const auth = useSelector((state) => state.auth);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const settings = useSelector((state) => state.settingsdata.settings);


  const LogOut = () => {
    dispatch(signOff());
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (url: string) => {
    if (url === 'logout') return false;
    const currentPath = location.pathname;
    return currentPath === url || (url !== '/' && currentPath.startsWith(url));
  };

  const menuItems = [
    { name: t('dashboard_text'), url: '/dashboard', icon: <DashboardIcon />, access: ['admin', 'fleetadmin'] },
    { name: t('booking_history'), url: '/bookings', icon: <ViewListIcon />, access: ['admin', 'fleetadmin'] },
    ...(settings?.premiumFeatures?.REACT_APP_ENABLE_ADM_REQUEST_RIDE === true
      ? [{ name: t('ADM_ADD_BOOKING'), url: '/addbookings', icon: <ViewListIcon />, access: ['admin', 'fleetadmin'] },]
      : []
    ),
    { name: t('user'), url: '/users', icon: <EmojiPeopleIcon />, access: ['admin', 'fleetadmin'] },
    { name: t('car_type'), url: '/cartypes', icon: <CarIcon />, access: ['admin'] },
    { name: t('cars'), url: '/cars', icon: <CarIcon />, access: ['admin', 'fleetadmin'] },
    { name: t('cancellation_reasons'), url: '/cancelreasons', icon: <CancelScheduleSendIcon />, access: ['admin'] },
    ...(settings?.premiumFeatures?.REACT_APP_BANNERS === true
      ? [{ url: '/banners', access: ['admin'], name: t('banners'), icon: <CampaignSharp /> }]
      : []
    ),
    ...(settings?.REACT_APP_PAGOPAR_PLANS_ACTIVE === true
      ? [{ url: '/plans', access: ['admin'], name: t('plans'), icon: <CardGiftcard /> }]
      : []
    ),
    ...(settings?.premiumFeatures?.REACT_APP_ADM_SAVED_ADDRESSES === true
      ? [{ url: '/admSavedAddresses', access: ['admin'], name: t('saved_address'), icon: <Place /> }]
      : []
    ),

    { name: t('withdraws'), url: '/withdraws', icon: <MoneyIcon />, access: ['admin'] },
    { name: t('add_to_wallet'), url: '/addtowallet', icon: <AccountBalanceWalletIcon />, access: ['admin'] },
    { name: t('report'), url: '/allreports', icon: <AssessmentIcon />, access: ['admin', 'fleetadmin'] },
    { name: t('promo'), url: '/promos', icon: <OfferIcon />, access: ['admin'] },
    { name: t('push_notification_title'), url: '/notifications', icon: <NotifyIcon />, access: ['admin'] },
    { name: t('emergency_calls'), url: '/panics', icon: <NotifyIcon />, access: ['admin'] },
    { name: t('settings_title'), url: '/settings', icon: <PhoneIphoneIcon />, access: ['admin'] },
    { name: t('logout'), url: 'logout', icon: <ExitIcon />, access: ['admin', 'fleetadmin'] }
  ];

  const drawerContent = (
    <Box
      sx={{
        minHeight: '100dvh',
        backgroundColor: colors.card,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '0.3rem',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-track': {
          background: colors.card,
        },
        '&::-webkit-scrollbar-thumb': {
          background: colors.primary,
          borderRadius: '20px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: colors.white,
          borderRadius: '20px',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, }}
      >
        <Box
          alt="Logo"
          component="img"
          src={`${process.env.PUBLIC_URL}/logo-1024x1024.png`}
          sx={{ width: '120px', height: '120px', borderRadius: '20px', objectFit: 'cover' }}
        />
      </Box>

      {auth.profile?.usertype && (
        <List disablePadding>
          {menuItems.map((item, index) => {
            if (!item.access.includes(auth.profile?.usertype!)) return null;

            const active = isActive(item.url);
            const isLogout = item.url === 'logout';

            return (
              <ListItem key={`menu-item-${index}`} disablePadding>
                <ListItemButton
                  component={isLogout ? 'div' : Link}
                  to={isLogout ? undefined : item.url}
                  onClick={isLogout ? LogOut : undefined}
                  sx={{
                    mx: 1.5,
                    my: 0.5,
                    borderRadius: 2,
                    justifyContent: 'flex-start',
                    backgroundColor: active ? `${colors.primary}80` : 'transparent',
                    transition: theme.transitions.create(['background-color', 'transform'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                    '&:hover': {
                      backgroundColor: active
                        ? `${colors.primary}80`
                        : `${colors.primary}60`,
                      transform: 'translateX(4px)',
                      '& .MuiSvgIcon-root': {
                        color: colors.white,
                      },
                      '& .MuiTypography-root': {
                        color: colors.white,
                      }
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 48,
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': {
                        color: active ? colors.white : colors.text,
                        transition: theme.transitions.create('color', {
                          duration: theme.transitions.duration.shorter,
                        }),
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: active ? 600 : 400,
                        color: active ? colors.white : colors.text,
                        transition: theme.transitions.create('color', {
                          duration: theme.transitions.duration.shorter,
                        }),
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        sx={{
          backgroundColor: colors.card,
          ml: { sm: `${DRAWER_WIDTH}px` },
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ minHeight: TOOLBAR_HEIGHT }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { sm: 'none' },
              transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.shorter,
              }),
              '&:hover': {
                transform: 'scale(1.1)',
              }
            }}
          >
            <MenuOpen />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontWeight: 600,
              background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.primary} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            {settings && settings.appName ? settings.appName : 'Admin Dashboard'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
        aria-label="navigation drawer"
      >
        <Drawer
          anchor="left"
          open={mobileOpen}
          variant="temporary"
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              overflow: 'hidden',
              borderRight: 'none',
              borderRadius: '0px 25px 25px 0px',
              top: 0,
              height: '100vh',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        <Drawer
          anchor="left"
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              overflow: 'hidden',
              borderRight: 'none',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              backgroundColor: colors.card,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          backgroundColor: (theme) => theme.palette.background.default,
          transition: theme.transitions.create(['width', 'margin', 'padding'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box sx={{ height: TOOLBAR_HEIGHT }} />

        {children ? children : <Outlet />}
      </Box>
    </Box>
  );
}